/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { StockUnit } from "./types";

type Props = {
  baseUnit: string;
  units: StockUnit[];
  onChange: (units: StockUnit[]) => void;
};

// Rows of "1 <name> = <qty> <base unit>", shared by the add-item and
// edit-units modals.
export const UnitsEditor = defineComponent<Props>({
  name: "UnitsEditor",
  props: ["baseUnit", "units", "onChange"],
  setup(props) {
    const { t } = useLocalization();

    const update = (index: number, patch: Partial<StockUnit>) =>
      props.onChange(props.units.map((u, i) => (i === index ? { ...u, ...patch } : u)));

    return () => (
      <div class="space-y-2">
        {props.units.map((unit, index) => (
          <div key={index} class="flex items-center gap-2">
            <span class="text-sm text-base-content/60">1</span>
            <input
              class="input input-bordered input-sm w-32"
              placeholder={t("inventory.stock.unit_name_placeholder")}
              value={unit.name}
              onInput={(e: Event) =>
                update(index, { name: (e.target as HTMLInputElement).value })
              }
            />
            <span class="text-sm text-base-content/60">=</span>
            <input
              type="number"
              min="0"
              step="any"
              class="input input-bordered input-sm w-28"
              value={unit.base_qty || ""}
              onInput={(e: Event) =>
                update(index, { base_qty: Number((e.target as HTMLInputElement).value) })
              }
            />
            <span class="text-sm text-base-content/60">{props.baseUnit}</span>
            <button
              class="btn btn-ghost btn-xs btn-square"
              aria-label={t("inventory.stock.remove_unit")}
              onClick={() => props.onChange(props.units.filter((_, i) => i !== index))}
            >
              <span class="material-symbols-outlined text-base">close</span>
            </button>
          </div>
        ))}
        <button
          class="btn btn-ghost btn-sm"
          onClick={() => props.onChange([...props.units, { name: "", base_qty: 0 }])}
        >
          <span class="material-symbols-outlined text-base">add</span>
          {t("inventory.stock.add_unit")}
        </button>
      </div>
    );
  },
});

export const validUnits = (units: StockUnit[]) =>
  units.filter((u) => u.name.trim() && u.base_qty > 0);
